import { AsyncData, Result } from "@swan-io/boxed";
import { Pressable, PressableProps, StyleSheet, TextProps, View, ViewProps } from "react-native";
import { match } from "ts-pattern";
import { Except } from "type-fest";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { Icon } from "./Icon";
import { Space } from "./Space";
import { PressableLink, PressableLinkProps, Text } from "./Text";

const styles = StyleSheet.create({
  button: {
    display: "flex",
    borderRadius: radii[4],
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    userSelect: "none",
    outlineWidth: 0,
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
  },
  regularButton: {
    paddingHorizontal: 16,
    height: 40,
  },
  smallButton: {
    paddingHorizontal: 12,
    height: 28,
  },
  miniButton: {
    paddingHorizontal: 10,
    height: 24,
  },
  regularIconOnly: {
    paddingHorizontal: 10,
  },
  smallIconOnly: {
    paddingHorizontal: 8,
  },
  miniIconOnly: {
    paddingHorizontal: 6,
  },
  plainButton: {
    backgroundImage:
      "linear-gradient(98.27deg, rgba(255, 255, 255, 0) -63.78%, rgba(255, 199, 153, 1) 100%)",
    backgroundColor: invariantColor.white,
  },
  plainButtonHover: {
    backgroundColor: colors.primary[500],
  },
  plainButtonPressed: {
    backgroundColor: colors.primary[300],
    transform: "scale(0.99)",
  },
  plainButtonDisabled: {
    backgroundImage: "none",
    backgroundColor: colors.neutral[300],
  },
  plainButtonSuccess: {},
  outlineButton: {
    borderWidth: 1,
    backgroundColor: colors.primary[900],
    borderColor: colors.primary[500],
  },
  outlineButtonHover: {
    backgroundColor: colors.primary[800],
  },
  outlineButtonPressed: {
    backgroundColor: colors.primary[700],
    transform: "scale(0.99)",
  },
  outlineButtonDisabled: {
    backgroundColor: colors.primary[900],
    borderColor: colors.primary[600],
  },
  outlineButtonSuccess: {
    borderColor: colors.positive[500],
  },
  negativeButton: {
    backgroundColor: colors.negative[500],
  },
  negativeButtonHover: {
    backgroundColor: colors.negative[400],
  },
  negativeButtonPressed: {
    backgroundColor: colors.negative[200],
    transform: "scale(0.99)",
  },
  negativeButtonDisabled: {
    backgroundColor: colors.neutral[300],
  },
  negativeButtonSuccess: {},
  transparentButton: {
    borderWidth: 1,
    backgroundColor: invariantColor.transparent,
    borderColor: colors.neutral[700],
  },
  transparentButtonHover: {
    backgroundColor: colors.neutral[800],
  },
  transparentButtonPressed: {
    backgroundColor: colors.neutral[700],
    transform: "scale(0.99)",
  },
  transparentButtonDisabled: {
    backgroundColor: colors.neutral[300],
  },
  transparentButtonSuccess: {
    backgroundColor: invariantColor.transparent,
  },

  logWithButton: {
    paddingHorizontal: 24,
    height: 40,
    backgroundColor: invariantColor.white,
  },
  logWithButtonHover: {
    backgroundColor: "#f2f2f2",
  },
  logWithButtonPressed: {
    backgroundColor: colors.neutral[50],
  },

  iconContainer: {
    top: 1,
  },

  content: {
    top: -1,
    flexDirection: "row",
    alignItems: "center",
    transitionDuration: animDuration[200],
    transitionProperty: "opacity",
  },
  contentReverse: {
    flexDirection: "row-reverse",
  },
  contentHidden: {
    opacity: 0,
  },
  loader: {
    position: "absolute",
    width: 16,
    height: 4,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: "auto",
  },
  loaderDot: {
    position: "absolute",
    width: 4,
    height: 4,
    borderRadius: 3,
    backgroundColor: colors.neutral[900],
    animationKeyframes: {
      "0%": {
        transform: "scale(0.5)",
      },
      "50%": {
        transform: "scale(1)",
      },
      "100%": {
        transform: "scale(0.5)",
      },
    },
    animationDuration: "1000ms",
    animationIterationCount: "infinite",
  },
  loaderDot1: {
    top: 0,
    left: 0,
  },
  loaderDot2: {
    top: 0,
    left: 6,
    animationDelay: "100ms",
  },
  loaderDot3: {
    top: 0,
    right: 0,
    animationDelay: "200ms",
  },

  successContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    animationKeyframes: {
      "0%": {
        transform: "scale(0)",
        borderRadius: 40,
      },
      "50%": {
        transform: "scale(1.2)",
      },
      "100%": {
        transform: "scale(1)",
        borderRadius: 0,
      },
    },
    animationDuration: "400ms",
  },
  plainSuccessContainer: {
    backgroundColor: colors.positive[500],
  },
  outlineSuccessContainer: {
    backgroundColor: colors.positive[500],
  },
  negativeSuccessContainer: {
    backgroundColor: colors.negative[500],
  },
  transparentSuccessContainer: {
    backgroundColor: invariantColor.transparent,
  },

  startSelfAlign: {
    alignSelf: "flex-start",
  },
  centerSelfAlign: {
    alignSelf: "center",
  },
  endSelfAlign: {
    alignSelf: "flex-end",
  },
  stretchSelfAlign: {},
});

const loaderColor: Record<ButtonVariant, string> = {
  plain: colors.neutral[900],
  outline: colors.primary[500],
  negative: colors.neutral[900],
  transparent: colors.neutral[50],
};

export type ButtonVariant = "plain" | "outline" | "negative" | "transparent";

export type ButtonProps = Except<PressableProps, "children" | "style"> & {
  size?: "mini" | "small" | "regular";
  variant?: ButtonVariant;
  status?: AsyncData<Result<unknown, unknown>>;
  icon?: IconName;
  iconFollowsText?: boolean;
  children?: string;
  selfAlign?: "start" | "center" | "end" | "stretch";
  style?: ViewProps["style"];
};

export const Button: React.FC<ButtonProps> = ({
  size = "regular",
  variant = "plain",
  status,
  icon,
  iconFollowsText = false,
  disabled = false,
  children,
  selfAlign = "stretch",
  style,
  ...props
}) => {
  const isRegular = size === "regular";
  const isLoading = status?.isLoading() ?? false;
  const isSuccess = (status?.isDone() && status.get().isOk()) ?? false;
  const isInteractive = !isLoading && !isSuccess && disabled !== true;
  const iconOnly = children == null;

  const textColor = match({ variant, disabled })
    .with({ variant: "outline", disabled: false }, () => colors.primary[200])
    .with({ variant: "outline", disabled: true }, () => colors.primary[600])
    .with({ variant: "plain", disabled: true }, () => colors.neutral[800])
    .with({ variant: "negative" }, () => colors.neutral[900])
    .with({ variant: "transparent", disabled: true }, () => colors.neutral[800])
    .with({ variant: "transparent" }, () => colors.neutral[50])
    .otherwise(() => colors.primary[900]);

  return (
    <Pressable
      style={({ focused, hovered, pressed }) => [
        styles.button,
        styles[`${size}Button`],
        styles[`${variant}Button`],
        iconOnly && styles[`${size}IconOnly`],
        (hovered || focused) && isInteractive && styles[`${variant}ButtonHover`],
        pressed && styles[`${variant}ButtonPressed`],
        disabled && styles[`${variant}ButtonDisabled`],
        isSuccess && styles[`${variant}ButtonSuccess`],
        styles[`${selfAlign}SelfAlign`],
        style,
      ]}
      disabled={!isInteractive}
      {...props}
    >
      {isLoading && <ButtonLoader color={loaderColor[variant]} />}
      {isSuccess && <SuccessOverlay variant={variant} />}

      <View
        style={[
          styles.content,
          iconFollowsText && styles.contentReverse,
          (isLoading || isSuccess) && styles.contentHidden,
        ]}
      >
        {icon != null && (
          <>
            <View style={styles.iconContainer}>
              <Icon name={icon} color={textColor} size={isRegular ? 24 : 16} />
            </View>
            {!iconOnly && <Space width={4} />}
          </>
        )}

        <Text variant="smallFlat" color={textColor}>
          {children}
        </Text>
      </View>
    </Pressable>
  );
};

const ButtonLoader: React.FC<{ color: string }> = ({ color }) => {
  return (
    <View style={styles.loader}>
      <View style={[styles.loaderDot, styles.loaderDot1, { backgroundColor: color }]} />
      <View style={[styles.loaderDot, styles.loaderDot2, { backgroundColor: color }]} />
      <View style={[styles.loaderDot, styles.loaderDot3, { backgroundColor: color }]} />
    </View>
  );
};

const SuccessOverlay: React.FC<{ variant: ButtonVariant }> = ({ variant }) => {
  const iconColor = match(variant)
    .with("transparent", () => colors.neutral[50])
    .otherwise(() => colors.neutral[900]);

  return (
    <View style={[styles.successContainer, styles[`${variant}SuccessContainer`]]}>
      <View>
        <Icon name="check" color={iconColor} size={24} />
      </View>
    </View>
  );
};

type LogWithButtonProps = Except<PressableProps, "children" | "style"> & {
  provider: "google" | "microsoft" | "github";
  children: string;
  status?: AsyncData<unknown>;
  style?: ViewProps["style"];
};

export const LogWithButton: React.FC<LogWithButtonProps> = ({
  provider,
  children,
  status,
  style,
  ...props
}) => {
  const isLoading = status?.isLoading() ?? false;

  return (
    <Pressable
      style={({ focused, hovered, pressed }) => [
        styles.button,
        styles.logWithButton,
        (hovered || focused) && styles.logWithButtonHover,
        pressed && styles.logWithButtonPressed,
        style,
      ]}
      {...props}
    >
      {isLoading && <ButtonLoader color={loaderColor.plain} />}

      <View style={[styles.content, isLoading && styles.contentHidden]}>
        <Icon name={`logo_${provider}`} size={24} />
        <Space width={8} />

        <Text variant="smallFlat" color="#1F1F1F">
          {children}
        </Text>
      </View>
    </Pressable>
  );
};

type ButtonLinkProps = Except<PressableLinkProps, "children" | "variant" | "style"> & {
  size?: "small" | "regular";
  variant?: ButtonVariant;
  icon?: IconName;
  iconFollowsText?: boolean;
  children?: string;
  selfAlign?: "start" | "center" | "end" | "stretch";
  style?: TextProps["style"];
};

export const ButtonLink: React.FC<ButtonLinkProps> = ({
  size = "regular",
  variant = "plain",
  icon,
  iconFollowsText = false,
  disabled = false,
  children,
  selfAlign = "stretch",
  style,
  ...props
}) => {
  const isInteractive = disabled !== true;
  const iconOnly = children == null;

  const textColor = match({ variant, disabled })
    .with({ variant: "outline", disabled: false }, () => colors.primary[200])
    .with({ variant: "outline", disabled: true }, () => colors.primary[600])
    .with({ variant: "plain", disabled: true }, () => colors.neutral[800])
    .with({ variant: "negative" }, () => colors.neutral[800])
    .with({ variant: "transparent", disabled: true }, () => colors.neutral[800])
    .with({ variant: "transparent" }, () => colors.neutral[50])
    .otherwise(() => colors.primary[900]);

  return (
    <PressableLink
      style={({ focused, hovered, pressed }) => [
        styles.button,
        styles[`${size}Button`],
        styles[`${variant}Button`],
        iconOnly && styles[`${size}IconOnly`],
        (hovered || focused) && isInteractive && styles[`${variant}ButtonHover`],
        pressed && styles[`${variant}ButtonPressed`],
        disabled && styles[`${variant}ButtonDisabled`],
        styles[`${selfAlign}SelfAlign`],
        style,
      ]}
      disabled={!isInteractive}
      {...props}
    >
      <View style={[styles.content, iconFollowsText && styles.contentReverse]}>
        {icon != null && (
          <>
            <View style={styles.iconContainer}>
              <Icon name={icon} color={textColor} size={16} />
            </View>
            {!iconOnly && <Space width={4} />}
          </>
        )}

        <Text variant="smallFlat" color={textColor}>
          {children}
        </Text>
      </View>
    </PressableLink>
  );
};
